import React, { useState } from "react";
import { ButtonGroup, Col, Row, ToggleButton } from "react-bootstrap";
import { ReactSelect } from "@services/ui-components";
import { useTranslation } from "react-i18next";

import { PeriodCode, TariffPlanBtnInterface, tariffPlansCurrency, TariffPlansFilter } from "../interface";
import { stylesSelect } from "@me-team/common/src/components/TariffManagement/TariffPlans/stylesSelectReact";
import { currencyOptions, defaultCurrency } from "./helpers/constants";

interface TariffPlanFilterProps {
    onFilterChange: (filter: TariffPlansFilter) => void;
    hideTitle?: boolean;
    btnStylesClass?: TariffPlanBtnInterface;
}

const TariffPlanFilter: React.FC<TariffPlanFilterProps> = ({ onFilterChange, hideTitle, btnStylesClass }) => {
    const { t } = useTranslation();
    const [filter, setFilter] = useState<TariffPlansFilter>({
        currency: defaultCurrency,
        period: "YEARLY",
    });

    const updateFilter = (nextFilter: TariffPlansFilter) => {
        setFilter(nextFilter);
        onFilterChange(nextFilter);
    };

    const handleCurrencyChange = (selectedOption: tariffPlansCurrency | null) => {
        const nextFilter: TariffPlansFilter = {
            ...filter,
            currency: selectedOption ?? defaultCurrency,
        };
        updateFilter(nextFilter);
    };

    const handlePeriodChange = (period: PeriodCode) => {
        const nextFilter: TariffPlansFilter = { ...filter, period };
        updateFilter(nextFilter);
    };

    const getButtonVariant = (
        buttonPeriod: PeriodCode,
        activePeriod: PeriodCode,
        btnStylesClass?: TariffPlanBtnInterface
    ) => {
        const isActive = buttonPeriod === activePeriod;
        const activeVariant = btnStylesClass?.activeButton || "orange text-white";
        const inactiveVariant = btnStylesClass?.inactiveButton || "outline-orange";
        return isActive ? activeVariant : inactiveVariant;
    };

    return (
        <Row className="d-flex align-items-center">
            {!hideTitle && (
                <Col xl={6}>
                    <h4 className="fw-normal m-xl-0 mb-2 align-items-center">
                        {t("Choose the Best Rate for Your Business!")}
                    </h4>
                </Col>
            )}
            <Col xl={hideTitle ? 12 : 6} className="d-flex align-items-center gap-4 justify-content-end">
                <div>
                    <ReactSelect
                        id="tariffFilter-currency-field"
                        value={filter.currency}
                        options={currencyOptions}
                        placeholder="Select currency"
                        onChange={handleCurrencyChange}
                        isSearchable={false}
                        stylesSelect={stylesSelect}
                    />
                </div>
                <ButtonGroup className="landing d-flex flex-nowrap h-100" style={{ minHeight: "32px" }}>
                    <ToggleButton
                        id="tariffFilter-annualSubscription-field"
                        type="radio"
                        variant={getButtonVariant("YEARLY", filter.period, btnStylesClass)}
                        name="subscriptionTerm"
                        value="YEARLY"
                        checked={filter.period === "YEARLY"}
                        onChange={() => handlePeriodChange("YEARLY")}
                        className="rounded-end-0 py-1 fw-normal d-flex align-items-center"
                    >
                        <i className="bo bi-star-fill me-1 me-sm-2"></i>
                        <span>{t("Annual Subscription")}</span>
                    </ToggleButton>
                    <ToggleButton
                        id="tariffFilter-monthlySubscription-field"
                        type="radio"
                        variant={getButtonVariant("MONTHLY", filter.period, btnStylesClass)}
                        name="subscriptionTerm"
                        value="MONTHLY"
                        checked={filter.period === "MONTHLY"}
                        onChange={() => handlePeriodChange("MONTHLY")}
                        className="rounded-start-0 py-1 fw-normal  d-flex align-items-center"
                    >
                        {t("Monthly Subscription")}
                    </ToggleButton>
                </ButtonGroup>
            </Col>
        </Row>
    );
};

export default TariffPlanFilter;
